/**
 * RelatoriosPage - Central de Relatórios Gerais
 *
 * 6 real operational reports with shared filters and Excel export.
 */
import { useEffect, useRef, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  CalendarCheck,
  BarChart3,
  History,
  FileSpreadsheet,
  Package,
  AlertTriangle,
  ArrowLeft,
  Play,
  Download,
  Loader2,
} from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { MainLayout } from '../components/MainLayout';
import PermissionGate from '../components/PermissionGate';
import { useRelatorioGeraisFilters } from '../hooks/useRelatorioGeraisFilters';
import { useRelatorioGeraisData, type ReportId } from '../hooks/useRelatorioGeraisData';
import {
  RelatorioGeraisFilters,
  AgendamentosReport,
  ConsolidadoQtdeReport,
  Ultimas3DetalhadoReport,
  ConsolidadoDetalhadoReport,
  SegregacaoConsolidadoReport,
  SolicitacaoColetaReport,
  ConsolidadoLeituraReport,
} from '../components/relatorios-gerais';

// Report definitions
interface ReportDef {
  id: ReportId;
  title: string;
  description: string;
  icon: any;
  category: string;
}

const reports: ReportDef[] = [
  {
    id: 'agendamentos',
    title: 'Agendamentos',
    description: 'Contagens agendadas no período com status e auditores',
    icon: CalendarCheck,
    category: 'Operacional',
  },
  {
    id: 'consolidado-qtde',
    title: 'Consolidado (qtde)',
    description: 'Quantidades consolidadas: sistema vs contado com acuracidade',
    icon: BarChart3,
    category: 'Análise',
  },
  // ⛔ N180 (Alana, 26/08): "Retirar o relatório Últimas 3 contagens que não
  // possui os dados de lote (manter apenas o Últimas 3 contagens detalhada)".
  // O agregado saiu da lista de relatórios; o detalhado abaixo é o que fica.
  //
  // Removido só da UI de propósito. A rota e o serviço continuam de pé
  // (api_relatorios_gerais.py:112-130, relatorios_gerais_service.py:491) porque
  // apagá-los obriga a mexer em tenant_manifest.py e em dois testes de
  // isolamento multi-tenant (test_relatorios_gerais_isolamento_cliente.py,
  // test_n151_codigo_erp_hospital_cliente.py) — mexer em teste de isolamento
  // T30 para entregar uma remoção de menu é trocar risco alto por ganho zero.
  // A limpeza do backend é um PR separado, quando der para rodar a suíte.
  {
    id: 'ultimas-3-detalhado',
    title: 'Últimas 3 Contagens (Detalhado)',
    description: 'O mesmo comparativo aberto por produto, lote e validade',
    icon: History,
    category: 'Comparativo',
  },
{
    id: 'consolidado-detalhado',
    title: 'Consolidado Detalhado',
    description: 'Todos os itens por contagem com quantidades, preços e aging',
    icon: FileSpreadsheet,
    category: 'Análise',
  },
  {
    id: 'segregacao-consolidado',
    title: 'Segregação Consolidado',
    description: 'Itens coletados com faixa aging, motivo, lacre e O.C.',
    icon: AlertTriangle,
    category: 'Operacional',
  },
  {
    id: 'solicitacao-coleta',
    title: 'Coletas Consolidado',
    description: 'Conferências avulsas com ordens de coleta',
    icon: Package,
    category: 'Operacional',
  },
  {
    id: 'consolidado-leitura',
    title: 'Consolidado Leitura',
    description: 'Itens contados com indicação de leitura manual ou scanner',
    icon: BarChart3,
    category: 'Análise',
  },
];

export const RelatoriosPage = () => {
  const filters = useRelatorioGeraisFilters();
  const {
    selectedReport,
    setSelectedReport,
    reportData,
    loading,
    error,
    exporting,
    page,
    setPage,
    totalPages,
    totalRecords,
    generateReport,
    exportExcel,
    exportPdf,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
  } = useRelatorioGeraisData();

  const currentReport = reports.find(r => r.id === selectedReport);

  // Serialize current filters for change detection
  const filtersJson = useMemo(() => JSON.stringify(filters.buildApiFilters()), [
    filters.dataInicio, filters.dataFim,
    filters.selectedHospitals, filters.selectedClientes, filters.selectedAuditores,
    filters.selectedStatus, filters.selectedFaixaAging,
  ]);

  // Track previous values to distinguish report selection vs filter change
  const prevReportRef = useRef<string | null>(null);
  const prevFiltersJsonRef = useRef<string>(filtersJson);

  // Debounced refetch for filter changes (600ms for date typing)
  const debouncedRefetch = useDebouncedCallback(() => {
    if (selectedReport) {
      generateReport(filters.buildApiFilters());
    }
  }, 600);

  // Auto-fetch: on report selection (immediate) or filter change (debounced)
  useEffect(() => {
    if (!selectedReport) {
      // Back to card grid - cancel pending debounce
      debouncedRefetch.cancel();
      prevReportRef.current = null;
      prevFiltersJsonRef.current = filtersJson;
      return;
    }

    const reportChanged = prevReportRef.current !== selectedReport;
    const filtersChanged = prevFiltersJsonRef.current !== filtersJson;

    prevReportRef.current = selectedReport;
    prevFiltersJsonRef.current = filtersJson;

    if (reportChanged) {
      // Report card clicked → fetch immediately
      debouncedRefetch.cancel();
      generateReport(filters.buildApiFilters());
    } else if (filtersChanged) {
      // Filter changed on loaded report → debounced refetch
      debouncedRefetch();
    }
  }, [selectedReport, filtersJson]);

  const handleSelectReport = (id: ReportId) => {
    setSelectedReport(id);
  };

  const handleBack = () => {
    setSelectedReport(null);
  };

  const handleGenerate = () => {
    generateReport(filters.buildApiFilters());
  };

  const handleExport = () => {
    exportExcel(filters.buildApiFilters());
  };

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'agendamentos':
        return (
          <AgendamentosReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      case 'consolidado-qtde':
        return (
          <ConsolidadoQtdeReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      // N180: o case de 'ultimas-3-contagens' saiu junto com o item do menu —
      // sem entrada na lista nada mais seleciona esse id.
      case 'ultimas-3-detalhado':
        return (
          <Ultimas3DetalhadoReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
case 'consolidado-detalhado':
        return (
          <ConsolidadoDetalhadoReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      case 'segregacao-consolidado':
        return (
          <SegregacaoConsolidadoReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      case 'solicitacao-coleta':
        return (
          <SolicitacaoColetaReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      case 'consolidado-leitura':
        return (
          <ConsolidadoLeituraReport
            data={reportData}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Sidebar />
      <MainLayout>
        <div className="p-6">
          {!selectedReport ? (
            <>
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Central de Relatórios
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Selecione um relatório para visualizar dados reais do sistema
                </p>
              </div>

              {/* Report cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reports.map((report) => {
                  const Icon = report.icon;
                  return (
                    <button
                      key={report.id}
                      onClick={() => handleSelectReport(report.id)}
                      className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200 text-left"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 bg-blue-600 dark:bg-blue-700 rounded-lg">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300">
                            {report.category}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {report.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {report.description}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium text-sm">
                        <FileSpreadsheet className="w-4 h-4" />
                        Gerar Relatório
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div>
              {/* Back button + Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar
                  </button>
                  {currentReport && (
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {currentReport.title}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {currentReport.description}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Gerar Relatório
                  </button>
                  {reportData.length > 0 && (
                    <PermissionGate tela="RELATORIOS_VIEW" acao="exportar">
                      <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Excel
                      </button>
                      {/* #12: PDF apenas no Consolidado (qtde) */}
                      {selectedReport === 'consolidado-qtde' && (
                        <button
                          onClick={() => exportPdf(filters.buildApiFilters())}
                          disabled={exporting}
                          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          {exporting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          PDF
                        </button>
                      )}
                    </PermissionGate>
                  )}
                </div>
              </div>

              {/* Filters */}
              <RelatorioGeraisFilters
                dataInicio={filters.dataInicio}
                setDataInicio={filters.setDataInicio}
                dataFim={filters.dataFim}
                setDataFim={filters.setDataFim}
                selectedHospitals={filters.selectedHospitals}
                setSelectedHospitals={filters.setSelectedHospitals}
                selectedClientes={filters.selectedClientes}
                setSelectedClientes={filters.setSelectedClientes}
                selectedAuditores={filters.selectedAuditores}
                setSelectedAuditores={filters.setSelectedAuditores}
                hospitalOptions={hospitalOptions}
                clienteOptions={clienteOptions}
                auditorOptions={auditorOptions}
                allFiltersExpanded={filters.allFiltersExpanded}
                setAllFiltersExpanded={filters.setAllFiltersExpanded}
                activeFilterCount={filters.activeFilterCount}
                clearAllFilters={filters.clearAllFilters}
                selectedReport={selectedReport}
                selectedStatus={filters.selectedStatus}
                setSelectedStatus={filters.setSelectedStatus}
                selectedFaixaAging={filters.selectedFaixaAging}
                setSelectedFaixaAging={filters.setSelectedFaixaAging}
              />

              {/* Error */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Report content */}
              {renderReportContent()}

              {/* Initial state - before first generation */}
              {!loading && reportData.length === 0 && !error && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Carregando dados... Se vazio, ajuste os filtros acima.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </MainLayout>
    </>
  );
};
